import getAnalyticsApi from '@custom/rtk-apis/billing/analytics/getAnalytics';
import { AppDispatch, RootState } from '@custom/store/store';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useTheme } from '@ohif/ui';

const ReportAnalytics = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();

  const { result: reportAnalyticsData, loading, error, status, msg } = useSelector(
    (state: RootState) => state.getAnalyticsResponse || {
      result: null,
      loading: false,
      error: '',
      status: '',
      msg: ''
    }
  );

  const today = new Date();
  const [filterType, setFilterType] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily');
  const [dateRange, setDateRange] = useState(() => {
    const currentDate = new Date();
    return {
      startDate: currentDate.toISOString().split('T')[0],
      endDate: currentDate.toISOString().split('T')[0],
    };
  });

  const updateDateRangeBasedOnFilter = (filter: 'daily' | 'weekly' | 'monthly' | 'yearly') => {
    const newDateRange = { startDate: '', endDate: '' };
    const currentDate = new Date();

    switch (filter) {
      case 'yearly': {
        const startDate = new Date(currentDate);
        startDate.setFullYear(currentDate.getFullYear() - 1);

        newDateRange.startDate = startDate.toISOString().split('T')[0];
        newDateRange.endDate = currentDate.toISOString().split('T')[0];
        break;
      }
      case 'monthly': {
        const startDate = new Date(currentDate);
        startDate.setMonth(currentDate.getMonth() - 1);
        startDate.setDate(0);

        newDateRange.startDate = startDate.toISOString().split('T')[0];
        newDateRange.endDate = currentDate.toISOString().split('T')[0];
        break;
      }
      case 'weekly': {
        const startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - 7);

        newDateRange.startDate = startDate.toISOString().split('T')[0];
        newDateRange.endDate = currentDate.toISOString().split('T')[0];
        break;
      }
      case 'daily': {
        newDateRange.startDate = currentDate.toISOString().split('T')[0];
        newDateRange.endDate = currentDate.toISOString().split('T')[0];
        break;
      }
    }

    setDateRange(newDateRange);
    return newDateRange;
  };

  const handleFetchAnalytics = (
    range: { startDate: string; endDate: string },
    type: 'daily' | 'weekly' | 'monthly' | 'yearly'
  ) => {
    const analyticsPayload = {
      startDate: range.startDate,
      endDate: range.endDate,
      filterType: type
    };
    dispatch(getAnalyticsApi(analyticsPayload));
  };

  const handleDateChange = (
    date: dayjs.Dayjs | null,
    field: 'startDate' | 'endDate'
  ) => {
    if (date) {
      setDateRange(prev => ({
        ...prev,
        [field]: date.format('YYYY-MM-DD')
      }));
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFilterType = e.target.value as 'daily' | 'weekly' | 'monthly' | 'yearly';
    setFilterType(newFilterType);
    const newRange = updateDateRangeBasedOnFilter(newFilterType);
    handleFetchAnalytics(newRange, newFilterType);
  };

  useEffect(() => {
    const initialDateRange = dateRange;
    handleFetchAnalytics(initialDateRange, filterType);
  }, []);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      handleFetchAnalytics(dateRange, filterType);
    }
  }, [dateRange.startDate, dateRange.endDate, filterType]);

  const generateChartOptions = (data: any, type: 'daily' | 'weekly' | 'monthly' | 'yearly') => {
    if (!data || !data[type]) {
      return {
        title: { text: 'Revenue Report' },
        series: [],
        xAxis: { categories: [] },
        subtitle: { text: 'No data available for the selected period' }
      };
    }

    const typeData = data[type];
    const categories = Object.keys(typeData);
    const seriesData = Object.values(typeData) as number[];

    return {
      chart: {
        type: 'column',
        backgroundColor: isDarkMode ? '#090c29' : '#F7F9FB',
        style: { fontFamily: 'sans-serif' }
      },
      title: {
        text: 'Revenue Report',
        align: 'left',
        style: { fontSize: '16px', color: isDarkMode ? '#fff' : '#333' }
      },
      xAxis: {
        categories,
        gridLineWidth: 0,
        labels: { style: { color: isDarkMode ? '#fff' : '#333' } }
      },
      yAxis: {
        title: { text: 'Revenue ($)', style: { color: isDarkMode ? '#fff' : '#333' } },
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            return `$${this.value.toLocaleString()}`;
          },
          style: { color: isDarkMode ? '#fff' : '#333' }
        },
        min: 0
      },
      tooltip: {
        backgroundColor: '#000',
        style: { color: '#fff' },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          return `Revenue: $${this.y.toLocaleString()}`;
        }
      },
      legend: { enabled: false },
      credits: {
        enabled: false
      },
      series: [
        {
          name: 'Revenue',
          type: 'column',
          data: seriesData,
          color: '#00C4B4',
          borderWidth: 0
        }
      ],
      plotOptions: {
        column: { pointWidth: 60, pointPadding: 0.2, borderWidth: 0 }
      }
    };
  };

  const isDataAvailable = !loading && !error;

  return (
    <div className="details" style={{ padding: '20px' }}>
      <h1 className='font-bold'>Analytics Dashboard</h1>

      <div style={{ marginBottom: '30px', border: '1px solid #ccc', padding: '15px', borderRadius: '4px' }}>
        <h2>Revenue Analytics</h2>

        <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ width: '200px' }}>
            <label htmlFor="startDate" style={{ marginRight: '10px', display: 'block', marginBottom: '5px', color: isDarkMode ? '#fff' : '#333' }}>
              Start Date:
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    backgroundColor: isDarkMode ? '#fff' : '#F6FFFF',
                    color: isDarkMode ? '#fff' : '#333',
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px',
                    color: isDarkMode ? '#fff' : '#333',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#fff' : '#ccc',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#333',
                  },
                }}
                value={dateRange.startDate ? dayjs(dateRange.startDate) : null}
                format="DD/MM/YYYY"
                disabled={true}
                onChange={(newValue) => handleDateChange(newValue, 'startDate')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputLabelProps: { style: { color: isDarkMode ? '#fff' : '#333' } },
                  },
                }}
                maxDate={dayjs(dateRange.endDate)}
              />
            </LocalizationProvider>
          </div>

          <div style={{ width: '200px' }}>
            <label htmlFor="endDate" style={{ marginRight: '10px', display: 'block', marginBottom: '5px', color: isDarkMode ? '#fff' : '#333' }}>
              End Date:
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    backgroundColor: isDarkMode ? '#fff' : '#F6FFFF',
                    color: isDarkMode ? '#fff' : '#333',
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px',
                    color: isDarkMode ? '#fff' : '#333',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#fff' : '#ccc',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#333',
                  },
                }}
                value={dateRange.endDate ? dayjs(dateRange.endDate) : null}
                format="DD/MM/YYYY"
                disabled={true}
                onChange={(newValue) => handleDateChange(newValue, 'endDate')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputLabelProps: { style: { color: isDarkMode ? '#fff' : '#333' } },
                  },
                }}
                minDate={dayjs(dateRange.startDate)}
              />
            </LocalizationProvider>
          </div>

          <div>
            <label htmlFor="filterType" style={{ marginRight: '10px', display: 'block', marginBottom: '5px', color: isDarkMode ? '#fff' : '#333' }}>
              Filter:
            </label>
            <select
              id="filterType"
              value={filterType}
              onChange={handleFilterChange}
              style={{
                padding: '8px',
                width: '120px',
                backgroundColor: isDarkMode ? '#090c29' : '#F6FFFF',
                color: isDarkMode ? '#fff' : '#333',
                border: `1px solid ${isDarkMode ? '#fff' : '#ccc'}`,
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '20px', padding: '20px', borderRadius: '8px' }}>
          {loading ? (
            <p>Loading...</p>
          ) : error ? (
            <p style={{ color: 'red' }}>No invoices found</p>
          ) : reportAnalyticsData ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={generateChartOptions(reportAnalyticsData, filterType)}
            />
          ) : (
            <p>Please select a date range and filter to view analytics.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportAnalytics;
